import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Badge,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  getReservationDetails,
  BASE_URL_for_images,
  getBillingById,
  updatependingamount,
} from "../../helpers/api.ts";
import { toast, ToastContainer } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import moment from "moment";
import SpaAppointmentStatusBadge from "../../customcomponents/SpaAppointmentStatusBadge";

const ReservationDetails = () => {
  const { reservationId, index } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [pendingPaymentModal, setPendingPaymentModal] = useState(false);
  const [pendingPaymentLoading, setPendingPaymentLoading] = useState(false);
  const [pendingPaymentForm, setPendingPaymentForm] = useState({
    received_amount: "",
    payment_mode: "",
    additional_note: "",
  });

  const [spaServices, setSpaServices] = useState([]);
  // ✅ FIX: Store full billing data fetched via getBillingById (same source as invoice)
  const [fullBillingData, setFullBillingData] = useState(null);

  const rd = (r) => r?.reservation_details || r?.reservationDetails || r?.reservation || r || {};
  const gd = (r) => r?.guest_details || r?.guestDetails || r?.guest || {};
  const roomd = (r) => r?.room_details || r?.roomDetails || r?.room || {};

  // ─────────────────────────────────────────────────────────────────────────
  // ✅ MASTER HELPER: Priority order:
  //   1. fullBillingData top-level (getBillingById spreads billing.toJSON() — SAME source as invoice)
  //   2. billing_details (from getReservationById — may have stale food_charges)
  //   3. reservation raw fields
  // ─────────────────────────────────────────────────────────────────────────
  const bd = (r) => {
    // getBillingById returns { ...billing.toJSON(), guest_details, billing_details, latest_details }
    // The top-level has the raw DB fields: food_charges, room_charges, final_amount, payment_mode etc.
    if (fullBillingData) return fullBillingData;
    return r?.billing_details || null;
  };

  const safeParseNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return isNaN(value) ? 0 : value;
    if (typeof value === "string") {
      const cleaned = value.trim();
      const numberMatch = cleaned.match(/^-?\d+(?:\.\d+)?/);
      if (numberMatch) {
        const parsed = parseFloat(numberMatch[0]);
        if (!isNaN(parsed)) return parsed;
      }
      const anyNumberMatch = cleaned.match(/\d+(?:\.\d+)?/);
      if (anyNumberMatch) {
        const parsed = parseFloat(anyNumberMatch[0]);
        if (!isNaN(parsed)) return parsed;
      }
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const r2 = (value) => Number(safeParseNumber(value).toFixed(2));

  useEffect(() => {
    fetchReservationDetails();
  }, [reservationId]);

  useEffect(() => {
    const fetchSpaServices = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const hotel_id = user?.role === "HOTEL_ADMIN" ? user?.hotel_id : user?.hotel?.hotel_id;
        if (!hotel_id) return;
        const { getSpaService } = await import("../../helpers/api.ts");
        const response = await getSpaService({ hotel_id });
        setSpaServices(response.spaServices || []);
      } catch (err) {
        console.error("Failed to fetch spa services:", err);
      }
    };
    fetchSpaServices();
  }, []);

  const getAppointmentServiceNames = (appointment) => {
    try {
      const raw = appointment?.service_ids;
      const ids = typeof raw === "string" ? JSON.parse(raw) : raw;
      const resolvedIds =
        Array.isArray(ids) && ids.length > 0
          ? ids
          : appointment?.service_id
          ? [appointment.service_id]
          : [];
      const names = resolvedIds
        .map((id) => spaServices.find((s) => s.id === id || s.id === Number(id))?.service_name)
        .filter(Boolean);
      return names.length > 0 ? names.join(", ") : appointment?.SpaService?.service_name || "N/A";
    } catch {
      return appointment?.SpaService?.service_name || "N/A";
    }
  };

  const fetchReservationDetails = async () => {
    try {
      setLoading(true);
      const response = await getReservationDetails(reservationId);
      if (response.success) {
        const data = response.data;
        console.log("✅ Reservation Details:", data);
        console.log("✅ billing_details from getReservationById:", data?.billing_details);
        setReservation(data);

        // ✅ CRITICAL FIX: If checked out, fetch full billing via getBillingById
        // This is the SAME source as the invoice/print page — guaranteed correct values
       const billingId = data?.billing_details?.id;
if (billingId) {   // ✅ Fetch billing whenever billingId exists — not just on CHECKED_OUT
  try {
    const billingResponse = await getBillingById(billingId);
    if (billingResponse?.success && billingResponse?.data) {
      console.log("✅ Full billing fetched via getBillingById:", billingResponse.data);
      setFullBillingData(billingResponse.data);
    }
  } catch (billingErr) {
    console.warn("⚠️ Could not fetch full billing, using billing_details fallback:", billingErr);
  }
}
      } else {
        toast.error("Failed to fetch reservation details");
      }
    } catch (error) {
      console.error("Error fetching reservation details:", error);
      toast.error("Error fetching reservation details");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintClick = async () => {
    try {
      setPrintLoading(true);
      const reservation_details = rd(reservation);
      const billingId =
        reservation?.billing_details?.id ||
        reservation_details?.billing_details?.id ||
        reservation?.billing_details;

      if (!billingId) {
        toast.error("Please verify whether the guest has checked out.");
        return;
      }

      const billingResponse = await getBillingById(billingId);
      if (billingResponse.success && billingResponse.data) {
        navigate(`/invoice/${billingId}`);
      } else {
        toast.info("No billing record found. Please create a billing record first.");
      }
    } catch (error) {
      console.error("Error checking billing:", error);
      toast.error("Error checking billing information");
    } finally {
      setPrintLoading(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case "CONFIRMED": return "success";
      case "PENDING": return "warning";
      case "CANCELLED": return "danger";
      default: return "secondary";
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // ✅ FIX: All charge helpers prefer billing_details (checkout form values)
  // ─────────────────────────────────────────────────────────────────────────

  /** Room charges: billing_details (edited in checkout form) → reservation tariff */
  const getRoomCharges = () => {
    const billing = bd(reservation);
    const reservation_details = rd(reservation);
    return safeParseNumber(
      billing?.room_charges ?? reservation_details?.tariff_charges ?? 0
    );
  };

  /** Food charges: billing_details (edited in checkout form) → reservation food_charges */
  const getFoodCharges = () => {
    const billing = bd(reservation);
    const reservation_details = rd(reservation);
    return safeParseNumber(
      billing?.food_charges ?? reservation_details?.food_charges ?? 0
    );
  };

  /** SPA charges: fullBillingData (same source as invoice) → billing_details spa → spa_billing_details → appointments */
  const calculateTotalSpaCharges = () => {
    if (!reservation) return 0;
    // ✅ CRITICAL FIX: Check fullBillingData FIRST — same source as invoice page
    // reservation.total_spa_charges may be stale; billing.spa_charges is what was billed at checkout
    const billing = bd(reservation);
    if (billing?.spa_charges != null && safeParseNumber(billing.spa_charges) > 0) {
      return safeParseNumber(billing.spa_charges);
    }
    // Fallback: sum from spa_billing_details records
    if (reservation?.spa_billing_details?.length > 0) {
      return reservation.spa_billing_details.reduce(
        (total, b) => total + safeParseNumber(b.final_amount), 0
      );
    }
    // Last resort: total_spa_charges from reservation
    if (reservation?.total_spa_charges) {
      return safeParseNumber(reservation.total_spa_charges);
    }
    const reservation_details = rd(reservation);
    return safeParseNumber(reservation_details?.spa_charges || 0);
  };

const getExtraBedCharges = () => {
  // ✅ PRIORITY 1: fullBillingData top-level (billing.toJSON() spread) — same source as invoice
  if (fullBillingData) {
    const val = safeParseNumber(
      fullBillingData?.extra_bed_charges ??
      fullBillingData?.extraBedCharges ??
      0
    );
    if (val > 0) return val;
  }

  // ✅ PRIORITY 2: billing_details (now includes extra_bed_charges after backend fix)
  const billing = bd(reservation);
  const fromBilling = safeParseNumber(billing?.extra_bed_charges);
  if (fromBilling > 0) return fromBilling;

  // ✅ PRIORITY 3: reservation object pre-checkout fallback
  return safeParseNumber(
    reservation?.reservation_details?.extrabedcharges ??
    reservation?.extrabedcharges ??
    0
  );
};

  /** Add-on charges (from billing only) */
const getAddOnCharges = () => {
  if (fullBillingData) {
    const val = safeParseNumber(fullBillingData?.add_on_charges ?? 0);
    if (val > 0) return val;
  }
  const billing = bd(reservation);
  const fromBilling = safeParseNumber(billing?.add_on_charges);
  if (fromBilling > 0) return fromBilling;
  return 0;
};
  /** Discount: billing_details first (user may have edited it in checkout form) */
  const getDiscount = () => {
    const billing = bd(reservation);
    const reservation_details = rd(reservation);
    return safeParseNumber(billing?.discount ?? reservation_details?.discount ?? 0);
  };

  /** Pending amount: billing_details first */
  const getPendingAmount = () => {
    const billing = bd(reservation);
    const reservation_details = rd(reservation);
    return safeParseNumber(billing?.pending_amount ?? reservation_details?.pending_amount ?? 0);
  };

  /** Advance paid */
  const getAdvancePaid = () => {
    const reservation_details = rd(reservation);
    return safeParseNumber(reservation_details?.Amount_Advance ?? 0);
  };

const buildReservationTotals = () => {
  const billingSource = bd(reservation);

  const roomBase = r2(getRoomCharges());
  const foodBase = r2(getFoodCharges());
  const spaBase = r2(calculateTotalSpaCharges());
  const addOnBase = r2(getAddOnCharges());
  const extraBedBase = r2(getExtraBedCharges());

  const roomGstRate = r2(billingSource?.room_gst) / 100;
  const foodGstRate = r2(billingSource?.food_gst) / 100;
  const spaGstRate = r2(billingSource?.spa_gst) / 100;

  const requestedDiscount = r2(getDiscount());
  const advancePaid = r2(getAdvancePaid());
  const pendingAmount = r2(getPendingAmount());

  const lines = [
    {
      key: "room",
      label: "Room Charges",
      base: roomBase,
      gstRate: roomGstRate,
      discountable: true,
    },
    {
      key: "food",
      label: "Food Charges",
      base: foodBase,
      gstRate: foodGstRate,
      discountable: true,
    },
    {
      key: "spa",
      label: "SPA Charges",
      base: spaBase,
      gstRate: spaGstRate,
      discountable: true,
    },
    {
      key: "addOn",
      label: "Add-on Charges",
      base: addOnBase,
      gstRate: 0,
      discountable: false,
    },
    {
      key: "extraBed",
      label: "Extra Bed Charges",
      base: extraBedBase,
      gstRate: 0,
      discountable: false,
    },
  ];

  const eligible = lines.filter((x) => x.discountable && x.base > 0);
  const eligibleSubtotal = r2(eligible.reduce((sum, x) => sum + x.base, 0));
  const baseAmount = r2(lines.reduce((sum, x) => sum + x.base, 0));
  const appliedDiscount = r2(Math.min(requestedDiscount, eligibleSubtotal));

  let usedDiscount = 0;
  const lastEligibleKey =
    eligible.length > 0 ? eligible[eligible.length - 1].key : null;

  const enriched = lines.map((line) => {
    let lineDiscount = 0;

    if (line.discountable && eligibleSubtotal > 0 && appliedDiscount > 0) {
      if (line.key === lastEligibleKey) {
        lineDiscount = r2(appliedDiscount - usedDiscount);
      } else {
        lineDiscount = r2((line.base / eligibleSubtotal) * appliedDiscount);
        usedDiscount = r2(usedDiscount + lineDiscount);
      }
    }

    const net = r2(line.base - lineDiscount);
    const gstAmount = r2(net * line.gstRate);
    const total = r2(net + gstAmount);

    return {
      ...line,
      discount: lineDiscount,
      net,
      gstAmount,
      total,
    };
  });

  const totalGst = r2(enriched.reduce((sum, x) => sum + x.gstAmount, 0));
  const netAfterDiscount = r2(baseAmount - appliedDiscount);
  const billTotal = r2(enriched.reduce((sum, x) => sum + x.total, 0));
  const finalAmountToPay = r2(
    Math.max(billTotal - advancePaid - pendingAmount, 0)
  );

  const byKey = Object.fromEntries(enriched.map((x) => [x.key, x]));

  return {
    lines: enriched,
    byKey,
    baseAmount,
    discount: appliedDiscount,
    netAfterDiscount,
    totalGst,
    billTotal,
    advancePaid,
    pendingAmount,
    finalAmountToPay,
  };
};

  const handleImageClick = (imageUrl, imageType) => {
    setSelectedImage({ url: imageUrl, type: imageType });
    setIsModalOpen(true);
  };

  const handleDownload = () => {
    if (selectedImage) {
      const link = document.createElement("a");
      link.href = selectedImage.url;
      link.download = `${selectedImage.type.replace(/\s+/g, "_")}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Image</title>
          <style>
            img { max-width: 100%; height: auto; }
            body { display: flex; justify-content: center; align-items: center; min-height: 100vh; margin: 0; }
          </style>
        </head>
        <body>
          <img src="${selectedImage.url}" alt="${selectedImage.type}" />
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handlePendingPaymentClick = () => {
    setPendingPaymentModal(true);
    const reservation_details = rd(reservation);
    setPendingPaymentForm({
      received_amount: getPendingAmount().toString() || "",
      payment_mode: reservation_details?.payment_mode || "",
      additional_note: "",
    });
  };

  const handlePendingPaymentSubmit = async () => {
    try {
      setPendingPaymentLoading(true);
      if (!reservationId) { toast.error("Reservation ID is required"); return; }
      if (!pendingPaymentForm.received_amount || Number(pendingPaymentForm.received_amount) <= 0) {
        toast.error("Received amount must be greater than 0");
        return;
      }
      const receivedAmount = Number(pendingPaymentForm.received_amount);
      const currentPendingAmount = getPendingAmount();
      if (receivedAmount > currentPendingAmount) {
        toast.error(`Received amount (${receivedAmount}) cannot be greater than pending amount (${currentPendingAmount})`);
        return;
      }
      const payload = {
        reservation_id: reservationId,
        received_amount: receivedAmount,
        payment_mode: pendingPaymentForm.payment_mode || undefined,
        additional_note: pendingPaymentForm.additional_note || undefined,
      };
      const response = await updatependingamount(payload);
      if (response.success) {
        toast.success(response.message || "Pending payment updated successfully!");
        setPendingPaymentModal(false);
        setPendingPaymentForm({ received_amount: "", payment_mode: "", additional_note: "" });
        await fetchReservationDetails();
      } else {
        toast.error(response.message || "Failed to update pending payment");
      }
    } catch (error) {
      console.error("Error updating pending payment:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Error updating pending payment. Please try again.");
      }
    } finally {
      setPendingPaymentLoading(false);
    }
  };

  const handlePendingPaymentFormChange = (field, value) => {
    setPendingPaymentForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center"><Spinner color="primary" /></div>
        </Container>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center"><h4>Reservation not found</h4></div>
        </Container>
      </div>
    );
  }

  const reservation_details = rd(reservation);
  const guest_details = gd(reservation);
  const room_details = roomd(reservation);
 const billing = bd(reservation);
const status = reservation_details?.status ?? "PENDING";
const urlParams = new URLSearchParams(window.location.search);
const index1 = urlParams.get("index");

const reservationTotals = buildReservationTotals();

const roomLine = reservationTotals.byKey.room || {
  base: 0, discount: 0, net: 0, gstAmount: 0, total: 0,
};
const foodLine = reservationTotals.byKey.food || {
  base: 0, discount: 0, net: 0, gstAmount: 0, total: 0,
};
const spaLine = reservationTotals.byKey.spa || {
  base: 0, discount: 0, net: 0, gstAmount: 0, total: 0,
};
const addOnLine = reservationTotals.byKey.addOn || {
  base: 0, discount: 0, net: 0, gstAmount: 0, total: 0,
};
const extraBedLine = reservationTotals.byKey.extraBed || {
  base: 0, discount: 0, net: 0, gstAmount: 0, total: 0,
};

const roomCharges = roomLine.base;
const foodCharges = foodLine.base;
const spaCharges = spaLine.base;
const extraBedCharges = extraBedLine.base;
const addOnCharges = addOnLine.base;

const baseAmount = reservationTotals.baseAmount;
const discount = reservationTotals.discount;
const netAfterDiscount = reservationTotals.netAfterDiscount;
const totalGst = reservationTotals.totalGst;
const billTotal = reservationTotals.billTotal;
const advancePaid = reservationTotals.advancePaid;
const pendingAmount = reservationTotals.pendingAmount;
const finalAmount = reservationTotals.finalAmountToPay;

  // Payment mode: prefer billing_details
  const paymentMode = billing?.payment_mode || reservation_details?.payment_mode || "N/A";

  return (
    <div className="page-content">
      <ToastContainer />
      <Container fluid>
        <Breadcrumbs title="Reservation Details" breadcrumbItem={`Reservation #${index1}`} />

        <div className="d-flex justify-content-end flex-column flex-md-row gap-2 mb-3">
          <Button color="secondary" onClick={() => navigate("/reservations")} className="w-100 w-md-auto">
            Back
          </Button>
          <Button color="primary" onClick={handlePrintClick} disabled={printLoading} className="w-100 w-md-auto">
            {printLoading ? <Spinner size="sm" /> : "Print"}
          </Button>
        </div>

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title">Reservation Information</h4>
                  <Badge color={getStatusBadgeColor(status)}>{status}</Badge>
                </div>

                <Row>
                  {/* ── Booking Details ─────────────────────────────── */}
                  <Col md={6}>
                    <Card className="bg-light">
                      <CardBody>
                        <h5 className="mb-4">Booking Details</h5>
                        <Row className="mb-3">
                          <Col sm={6}>
                            <p className="text-muted mb-2">Check In Date</p>
                            <h6>{moment(reservation_details.check_in_date).format("DD-MM-YYYY")}</h6>
                          </Col>
                          <Col sm={6}>
                            <p className="text-muted mb-2">Check Out Date</p>
                            <h6>{moment(reservation_details.check_out_date).format("DD-MM-YYYY")}</h6>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col sm={6}>
                            <p className="text-muted mb-2">Check In Time</p>
                            <h6>{reservation_details.check_in_time || "Not set"}</h6>
                          </Col>
                          <Col sm={6}>
                            <p className="text-muted mb-2">Check Out Time</p>
                            <h6>{reservation_details.check_out_time || "Not set"}</h6>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col sm={6}>
                            <p className="text-muted mb-2">Number of Nights</p>
                            <h6>{reservation_details.number_of_nights}</h6>
                          </Col>
                          <Col sm={6}>
                            <p className="text-muted mb-2">Guest Type</p>
                            <h6>{reservation_details.guest_type}</h6>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col sm={6}>
                            <p className="text-muted mb-2">Remarks</p>
                            <h6>{reservation_details.remark || "No remarks"}</h6>
                          </Col>
                          <Col sm={6}>
                            <p className="text-muted mb-2">Booking Source</p>
                            <h6>{reservation_details.booking_source}</h6>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm={12}>
                            <p className="text-muted mb-2">Additional Notes</p>
                            <h6>{reservation_details.additional_Note || "No additional notes"}</h6>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* ── Payment Information ─────────────────────────── */}
                  <Col md={6}>
                    <Card className="bg-light">
                      <CardBody>
                        <h5 className="mb-4">
                          Payment Information
                          {/* ✅ Badge shows data source for transparency */}
                          {(billing || fullBillingData) && (
                            <Badge color="success" className="ms-2" style={{ fontSize: "0.7rem" }}>
                              From Checkout Bill
                            </Badge>
                          )}
                        </h5>

                        {/* Charges Section */}
                        <div className="mb-4">
                          <h6 className="mb-3">Charges</h6>
                          <Row className="mb-3">
                            <Col sm={6}>
                              <p className="text-muted mb-2">Room Charges</p>
                              {/* ✅ From billing_details.room_charges */}
                              <h6>₹{roomCharges}</h6>
                            </Col>
                            <Col sm={6}>
                              <p className="text-muted mb-2">Food Charges</p>
                              {/* ✅ From billing_details.food_charges */}
                              <h6>₹{foodCharges}</h6>
                            </Col>
                            <Col sm={6}>
                              <p className="text-muted mb-2">SPA Charges</p>
                              <h6>
                                ₹{spaCharges}
                                {reservation?.spa_billing_details?.length > 0 && (
                                  <small className="text-muted ms-1">
                                    ({reservation.spa_billing_details.length} service{reservation.spa_billing_details.length > 1 ? "s" : ""})
                                  </small>
                                )}
                              </h6>
                            </Col>
                           {extraBedCharges > 0 && (
  <Col sm={6}>
    <p className="text-muted mb-2">Extra Bed Charges</p>
    <h6>₹{extraBedCharges}</h6>
  </Col>
)}
                        {addOnCharges > 0 && (
                              <Col sm={6}>
                                <p className="text-muted mb-2">Add-on Charges</p>
                                <h6>₹{addOnCharges}</h6>
                              </Col>
                            )}
                            <Col sm={6}>
                              <p className="text-muted mb-2">Discount</p>
                              {/* ✅ From billing_details.discount */}
                              <h6 className="text-danger">- ₹{discount}</h6>
                            </Col>
                          </Row>
                        </div>

                        {/* Payment Details Section */}
                        <div className="mb-4">
                          <h6 className="mb-3">Payment Details</h6>
                          <Row className="mb-3">
                            <Col sm={6}>
                              <p className="text-muted mb-2">Amount Status</p>
                              <Badge
                                color={
                                  reservation_details.Amount_Status === "PAID" ? "success"
                                  : reservation_details.Amount_Status === "PARTIALLY_PENDING" ? "warning"
                                  : reservation_details.Amount_Status === "ADVANCE_PAID" ? "info"
                                  : reservation_details.Amount_Status === "CANCELLED" ? "danger"
                                  : "secondary"
                                }
                              >
                                {reservation_details.Amount_Status || "N/A"}
                              </Badge>
                            </Col>
                            <Col sm={6}>
                              <p className="text-muted mb-2">Payment Mode</p>
                              {/* ✅ From billing_details.payment_mode */}
                              <h6>{paymentMode}</h6>
                            </Col>
                            <Col sm={6}>
                              <p className="text-muted mb-2">Advance Amount Paid</p>
                              <h6 className="text-success">₹{advancePaid}</h6>
                            </Col>
                            <Col sm={6}>
                              <p className="text-muted mb-2">Package/Plan</p>
                              <h6>{reservation_details.package || "N/A"}</h6>
                            </Col>
                            {pendingAmount > 0 && (
                              <Col sm={6}>
                                <p className="text-muted mb-2">Amount to be Paid Later</p>
                                {/* ✅ From billing_details.pending_amount */}
                                <h6 className="text-warning">₹{pendingAmount}</h6>
                              </Col>
                            )}
                          </Row>

                          {pendingAmount > 0 && (
                            <Row className="mt-3">
                              <Col sm={12}>
                                <Button color="success" size="sm" onClick={handlePendingPaymentClick}>
                                  <i className="bx bx-money me-1"></i>
                                  Record Pending Payment
                                </Button>
                                <small className="text-muted d-block mt-1">
                                  Click to record received payment for pending amount
                                </small>
                              </Col>
                            </Row>
                          )}
                        </div>

                        {/* ✅ Summary Section — ALL from billing_details */}
                        <div className="mb-4">
                         <div className="mb-4">
  <div className="border-top pt-3">
    <div className="d-flex justify-content-between mb-2">
      <p className="text-muted mb-0">Base Amount</p>
      <h6 className="mb-0">₹{baseAmount.toFixed(2)}</h6>
    </div>

    <div className="d-flex justify-content-between mb-2">
      <p className="text-muted mb-0">Discount</p>
      <h6 className="mb-0 text-danger">- ₹{discount.toFixed(2)}</h6>
    </div>

    <div className="d-flex justify-content-between mb-2">
      <p className="text-muted mb-0">Net Amount After Discount</p>
      <h6 className="mb-0">₹{netAfterDiscount.toFixed(2)}</h6>
    </div>

    <div className="d-flex justify-content-between mb-2">
      <p className="text-muted mb-0">Total GST</p>
      <h6 className="mb-0">₹{totalGst.toFixed(2)}</h6>
    </div>

    <div className="d-flex justify-content-between mb-2 border-top pt-2">
      <p className="text-muted mb-0"><strong>Bill Total (Net + GST)</strong></p>
      <h6 className="mb-0 text-dark">₹{billTotal.toFixed(2)}</h6>
    </div>

    {advancePaid > 0 && (
      <div className="d-flex justify-content-between mb-2">
        <p className="text-muted mb-0"><small>Less: Advance Paid</small></p>
        <h6 className="mb-0 text-success">- ₹{advancePaid.toFixed(2)}</h6>
      </div>
    )}

    {pendingAmount > 0 && (
      <div className="d-flex justify-content-between mb-2">
        <p className="text-muted mb-0"><small>Less: Pending Amount</small></p>
        <h6 className="mb-0 text-warning">- ₹{pendingAmount.toFixed(2)}</h6>
      </div>
    )}

    <div className="border-top pt-3 mt-2">
      <p className="text-muted mb-1">
        <strong>Final Amount to Pay Now</strong>
        <br />
        <small className="text-muted">
          After Discount, GST, Advance & Pending deduction
        </small>
      </p>
      <h4 className="text-success">₹{finalAmount.toFixed(2)}</h4>
    </div>
  </div>
</div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* ── Payment Summary Table ───────────────────────────── */}
                <Row className="mt-4">
                  <Col md={12}>
                    <Card className="bg-light">
                      <CardBody>
                        <h5 className="mb-4">Payment Summary</h5>
                        <Table responsive striped>
                          <thead>
                            <tr>
                              <th>Pending Received Amount</th>
                              <th>Pending Received Date</th>
                              <th>Additional Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                {safeParseNumber(reservation_details.pending_received_amount) > 0
                                  ? `₹${safeParseNumber(reservation_details.pending_received_amount)}`
                                  : "No payment received"}
                              </td>
                              <td>
                                {reservation_details.pending_received_date
                                  ? moment(reservation_details.pending_received_date).format("DD-MM-YYYY HH:mm:ss")
                                  : "N/A"}
                              </td>
                              <td>{reservation_details.additional_note || "No additional notes"}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* ── Guest & Room Info ───────────────────────────────── */}
                <Row className="mt-4">
                  <Col md={6}>
                    <Card className="bg-light">
                      <CardBody>
                        <h5 className="mb-4">Guest Information</h5>
                        <Row className="mb-3">
                          <Col sm={12}>
                            <p className="text-muted mb-2">Name</p>
                            <h6>{`${guest_details.first_name} ${guest_details.middle_name || ""} ${guest_details.last_name}`}</h6>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col sm={6}>
                            <p className="text-muted mb-2">Mobile</p>
                            <h6>{guest_details.mobile_number || "N/A"}</h6>
                          </Col>
                          <Col sm={6}>
                            <p className="text-muted mb-2">Email</p>
                            <h6>{guest_details.email || "N/A"}</h6>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col sm={6}>
                            <p className="text-muted mb-2">Gender</p>
                            <h6>{guest_details.gender}</h6>
                          </Col>
                          <Col sm={6}>
                            <p className="text-muted mb-2">Age</p>
                            <h6>{guest_details.age}</h6>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm={12}>
                            <p className="text-muted mb-2">Address</p>
                            <h6>
                              {guest_details.address}, {guest_details.city}, {guest_details.state},{" "}
                              {guest_details.country} - {guest_details.zip_code}
                            </h6>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card className="bg-light">
                      <CardBody>
                        <h5 className="mb-4">Room Information</h5>
                        <Row className="mb-3">
                          <Col sm={6}>
                            <p className="text-muted mb-2">Room Number</p>
                            <h6>{room_details.room_number}</h6>
                          </Col>
                          <Col sm={6}>
                            <p className="text-muted mb-2">Room Type</p>
                            <h6>{room_details.room_type}</h6>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col sm={6}>
                            <p className="text-muted mb-2">AC/Non-AC</p>
                            <h6>{room_details.ac_no_ac || "N/A"}</h6>
                          </Col>
                          <Col sm={6}>
                            <p className="text-muted mb-2">Tariff</p>
                            <h6>{room_details.tariff ? `₹${room_details.tariff}` : "N/A"}</h6>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm={12}>
                            <p className="text-muted mb-2">Status</p>
                            <Badge color={getStatusBadgeColor(room_details.roomstatus)}>
                              {room_details.roomstatus}
                            </Badge>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* ── ID Proofs ────────────────────────────────────────── */}
                <Row className="mt-4">
                  <Col md={12}>
                    <Card className="bg-light">
                      <CardBody>
                        <h5 className="mb-4">ID Proof & Photos</h5>

                        <div className="mb-5">
                          <h6 className="mb-3">
                            Main Guest - {`${guest_details.first_name || ""} ${guest_details.last_name || ""}`.trim()}
                          </h6>
                          <p className="text-muted mb-2">ID Proof Type</p>
                          <h6 className="mb-3">{reservation_details.id_proof_type}</h6>
                          <Row className="g-4">
                            <Col md={4}>
                              {reservation_details.id_proof_front && (
                                <div className="mb-4">
                                  <p className="text-muted mb-2">ID Proof Front</p>
                                  <div
                                    className="image-container"
                                    onClick={() => handleImageClick(`${BASE_URL_for_images}/${reservation_details.id_proof_front}`, "ID Proof Front")}
                                    style={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
                                  >
                                    <img src={`${BASE_URL_for_images}/${reservation_details.id_proof_front}`} alt="ID Proof Front" className="img-fluid" style={{ width: "100%", height: "200px", objectFit: "contain" }} />
                                  </div>
                                </div>
                              )}
                            </Col>
                            <Col md={4}>
                              {reservation_details.id_proof_back && (
                                <div>
                                  <p className="text-muted mb-2">ID Proof Back</p>
                                  <div
                                    className="image-container"
                                    onClick={() => handleImageClick(`${BASE_URL_for_images}/${reservation_details.id_proof_back}`, "ID Proof Back")}
                                    style={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
                                  >
                                    <img src={`${BASE_URL_for_images}/${reservation_details.id_proof_back}`} alt="ID Proof Back" className="img-fluid" style={{ width: "100%", height: "200px", objectFit: "contain" }} />
                                  </div>
                                </div>
                              )}
                            </Col>
                            <Col md={4}>
                              {reservation_details.guest_photo && (
                                <div>
                                  <p className="text-muted mb-2">Guest Photo</p>
                                  <div
                                    className="image-container"
                                    onClick={() => handleImageClick(`${BASE_URL_for_images}/${reservation_details.guest_photo}`, "Guest Photo")}
                                    style={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
                                  >
                                    <img src={`${BASE_URL_for_images}/${reservation_details.guest_photo}`} alt="guest" className="img-fluid" style={{ width: "100%", height: "200px", objectFit: "contain" }} />
                                  </div>
                                </div>
                              )}
                            </Col>
                          </Row>
                        </div>

                        {guest_details.partners && guest_details.partners.length > 0 && (
                          <div>
                            <h6 className="mb-3">Guest Partners</h6>
                            {guest_details.partners.map((partner) => (
                              <div key={partner.partner_id} className="mb-5">
                                <h6 className="mb-3">{partner.name} - {partner.relationship}</h6>
                                <p className="text-muted mb-2">ID Proof Type: {partner.id_proof_type}</p>
                                <Row className="g-4">
                                  <Col md={4}>
                                    {partner.id_proof_front && (
                                      <div>
                                        <p className="text-muted mb-2">ID Proof Front</p>
                                        <div className="image-container" onClick={() => handleImageClick(`${BASE_URL_for_images}/${partner.id_proof_front}`, `${partner.name} ID Front`)} style={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                                          <img src={`${BASE_URL_for_images}/${partner.id_proof_front}`} alt={`${partner.name} ID Front`} className="img-fluid" style={{ width: "100%", height: "200px", objectFit: "contain" }} />
                                        </div>
                                      </div>
                                    )}
                                  </Col>
                                  <Col md={4}>
                                    {partner.id_proof_back && (
                                      <div>
                                        <p className="text-muted mb-2">ID Proof Back</p>
                                        <div className="image-container" onClick={() => handleImageClick(`${BASE_URL_for_images}/${partner.id_proof_back}`, `${partner.name} ID Back`)} style={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                                          <img src={`${BASE_URL_for_images}/${partner.id_proof_back}`} alt={`${partner.name} ID Back`} className="img-fluid" style={{ width: "100%", height: "200px", objectFit: "contain" }} />
                                        </div>
                                      </div>
                                    )}
                                  </Col>
                                  <Col md={4}>
                                    {partner.partner_photo && (
                                      <div>
                                        <p className="text-muted mb-2">Partner Photo</p>
                                        <div className="image-container" onClick={() => handleImageClick(`${BASE_URL_for_images}/${partner.partner_photo}`, `${partner.name} Photo`)} style={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                                          <img src={`${BASE_URL_for_images}/${partner.partner_photo}`} alt={`${partner.name}`} className="img-fluid" style={{ width: "100%", height: "200px", objectFit: "contain" }} />
                                        </div>
                                      </div>
                                    )}
                                  </Col>
                                </Row>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── SPA Billing Details ─────────────────────────────────────── */}
        {reservation?.spa_billing_details && reservation.spa_billing_details.length > 0 && (
          <Row className="mt-4">
            <Col md={12}>
              <Card className="bg-light">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0">SPA Billing Details</h5>
                <Badge color="info" pill>
  Total: ₹{spaLine.total.toFixed(2)}
  <small className="ms-1" style={{ fontSize: "0.7rem" }}>(after discount + GST)</small>
</Badge>
                  </div>
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Invoice Number</th>
                        <th>Service Name</th>
                        <th>Base Amount</th>
                        <th>GST</th>
                        <th>Final Amount</th>
                        <th>Status</th>
                        <th>Billed Date</th>
                      </tr>
                    </thead>
                  <tbody>
  {reservation.spa_billing_details.map((b, idx) => {
    const effectiveGstRate =
      safeParseNumber(b.gst_percentage) > 0
        ? safeParseNumber(b.gst_percentage)
        : safeParseNumber(billing?.spa_gst ?? 0);

    const afterDiscount =
      safeParseNumber(b.subtotal || b.base_amount || 0) -
      safeParseNumber(b.discount || 0);

    const gstAmount =
      effectiveGstRate > 0
        ? Math.round((afterDiscount * effectiveGstRate) / 100)
        : 0;

    return (
      <tr key={b.id || idx}>
        <td>{idx + 1}</td>
        <td><Badge color="primary">{b.invoice_number || "N/A"}</Badge></td>
        <td>
          <strong>
            {(() => {
              const linkedApt = reservation?.spa_appointments?.find((a) => a.id === b.appointment_id);
              return linkedApt ? getAppointmentServiceNames(linkedApt) : b.service?.service_name || "N/A";
            })()}
          </strong>
        </td>
        <td>₹{safeParseNumber(b.subtotal || b.base_amount || 0)}</td>
        <td>
          {effectiveGstRate > 0
            ? `₹${gstAmount} (${effectiveGstRate}%)`
            : "₹0"}
        </td>
      <td><strong className="text-success">₹{afterDiscount + gstAmount}</strong></td>
        <td>
          <Badge color={b.payment_status === "PAID" ? "success" : b.payment_status === "PENDING" ? "warning" : "secondary"}>
            {b.payment_status || "N/A"}
          </Badge>
        </td>
        <td>{b.createdAt ? moment(b.createdAt).format("DD-MM-YYYY HH:mm") : "N/A"}</td>
      </tr>
    );
  })}
</tbody>

                  
<tfoot>
  <tr className="table-success">
    <th colSpan="3" className="text-end">Total SPA Charges (from Checkout Bill):</th>
    <th>
      ₹{spaLine.base.toFixed(2)}
      {spaLine.discount > 0 && (
        <small className="d-block text-danger">Disc: -₹{spaLine.discount.toFixed(2)}</small>
      )}
      {spaLine.discount > 0 && (
        <small className="d-block text-muted">Net: ₹{spaLine.net.toFixed(2)}</small>
      )}
    </th>
    <th>
      {spaLine.gstAmount > 0
        ? `₹${spaLine.gstAmount.toFixed(2)} (${safeParseNumber(billing?.spa_gst ?? 0)}%)`
        : "₹0"}
    </th>
    <th className="text-success">₹{spaLine.total.toFixed(2)}</th>
    <th colSpan="2"></th>
  </tr>
</tfoot>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* ── Food Orders ─────────────────────────────────────────────── */}
        {reservation.food_orders_detail && reservation.food_orders_detail.length > 0 && (
          <Row className="mt-4">
            <Col md={12}>
              <Card className="bg-light">
                <CardBody>
                  <h5 className="mb-4">Food Orders</h5>
                  <Table responsive striped>
                   <thead>
  <tr>
    <th>Dish Name</th>
    <th>Quantity</th>
    <th>Unit Price</th>
    <th>GST</th>
    <th>Subtotal</th>
    <th>Order Date</th>
  </tr>
</thead>
                    <tbody>
                     {reservation.food_orders_detail.map((order, idx) => {
  const unitCost = safeParseNumber(order.HotelMenu?.cost || order.cost || 0);
  const qty = safeParseNumber(order.quantity || 0);
  const lineSubtotal = unitCost * qty;
  const foodGstRate = safeParseNumber(
    order.gst_percentage ?? order.HotelMenu?.gst_percentage ?? billing?.food_gst ?? 0
  );
  const gstAmount = foodGstRate > 0 ? Math.round((lineSubtotal * foodGstRate) / 100) : 0;

  return (
    <tr key={order.menuId || idx}>
      <td><strong>{order.HotelMenu?.dish || order.dish || "N/A"}</strong></td>
      <td>{qty}</td>
      <td>₹{unitCost}</td>
      <td>
        {foodGstRate > 0
          ? <span>₹{gstAmount} <small className="text-muted">({foodGstRate}%)</small></span>
          : <span className="text-muted">—</span>
        }
      </td>
     <td><strong className="text-success">₹{(lineSubtotal + gstAmount).toFixed(2)}</strong></td>
      <td>{order.createdAt ? moment(order.createdAt).format("DD-MM-YYYY HH:mm") : "N/A"}</td>
    </tr>
  );
})}
                    </tbody>
          <tfoot>
  <tr className="table-success">
    <th colSpan="2" className="text-end">Total Food Charges (from Checkout Bill):</th>
    <th>
      ₹{foodLine.base.toFixed(2)}
      {foodLine.discount > 0 && (
        <small className="d-block text-danger">Disc: -₹{foodLine.discount.toFixed(2)}</small>
      )}
      {foodLine.discount > 0 && (
        <small className="d-block text-muted">Net: ₹{foodLine.net.toFixed(2)}</small>
      )}
    </th>
    <th>
      {foodLine.gstAmount > 0
        ? `₹${foodLine.gstAmount.toFixed(2)} (${safeParseNumber(billing?.food_gst ?? 0)}%)`
        : "₹0"}
    </th>
    <th className="text-success">₹{foodLine.total.toFixed(2)}</th>
    <th></th>
  </tr>
</tfoot>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* ── Image Preview Modal ──────────────────────────────────────── */}
        <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)} size="lg" className="modal-fullscreen-md-down">
          <ModalHeader toggle={() => setIsModalOpen(false)}>{selectedImage?.type}</ModalHeader>
          <ModalBody className="text-center">
            {selectedImage && (
              <img src={selectedImage.url} alt={selectedImage.type} className="img-fluid" style={{ maxHeight: "70vh" }} />
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleDownload}><i className="bx bx-download me-1"></i>Download</Button>
            <Button color="info" onClick={handlePrint}><i className="bx bx-printer me-1"></i>Print</Button>
            <Button color="secondary" onClick={() => setIsModalOpen(false)}>Close</Button>
          </ModalFooter>
        </Modal>

        {/* ── Pending Payment Modal ────────────────────────────────────── */}
        <Modal isOpen={pendingPaymentModal} toggle={() => setPendingPaymentModal(false)} size="md" className="modal-fullscreen-md-down">
          <ModalHeader toggle={() => setPendingPaymentModal(false)}>Record Pending Payment</ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label>Reservation ID</Label>
                <Input type="text" value={reservationId} disabled />
              </FormGroup>
              <FormGroup>
                <Label>Received Amount <span className="text-danger">*</span></Label>
                <Input
                  type="number"
                  value={pendingPaymentForm.received_amount}
                  onChange={(e) => handlePendingPaymentFormChange("received_amount", e.target.value)}
                  placeholder="Enter received amount"
                  min="0"
                  max={pendingAmount}
                  step="0.01"
                />
                <small className="text-muted">Maximum: ₹{pendingAmount}</small>
              </FormGroup>
              <FormGroup>
                <Label>Payment Mode</Label>
                <Input type="select" value={pendingPaymentForm.payment_mode} onChange={(e) => handlePendingPaymentFormChange("payment_mode", e.target.value)}>
                  <option value="">Select Payment Mode</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="NET_BANKING">Net Banking</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="OTHER">Other</option>
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Additional Note</Label>
                <Input type="textarea" value={pendingPaymentForm.additional_note} onChange={(e) => handlePendingPaymentFormChange("additional_note", e.target.value)} placeholder="Enter any additional notes (optional)" rows={3} />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setPendingPaymentModal(false)} disabled={pendingPaymentLoading}>Cancel</Button>
            <Button color="success" onClick={handlePendingPaymentSubmit} disabled={pendingPaymentLoading}>
              {pendingPaymentLoading ? <Spinner size="sm" /> : "Submit Payment"}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default ReservationDetails;
